import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';


const ReviewSubmitStep = ({ applicationData, onSubmit, onBack }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // Simulate application submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In real implementation, save to Supabase
      // const { error } = await supabase.from('partner_applications').insert({...});
      
      onSubmit?.();
    } catch (err) {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sections = [
    {
      title: 'Company Profile',
      icon: 'Building2',
      data: applicationData?.companyProfile,
      fields: [
        { label: 'Company Name', key: 'companyName' },
        { label: 'Legal Name', key: 'legalName' },
        { label: 'Year Established', key: 'yearEstablished' },
        { label: 'Headquarters', key: 'headquarters' },
        { label: 'Employee Count', key: 'employeeCount' },
        { label: 'Annual Revenue', key: 'annualRevenue' }
      ]
    },
    {
      title: 'Service Offerings',
      icon: 'Briefcase',
      data: applicationData?.serviceOfferings,
      fields: [
        { label: 'Primary Services', key: 'primaryServices', isArray: true },
        { label: 'Geographic Coverage', key: 'geographicCoverage', isArray: true },
        { label: 'Certifications', key: 'certifications', isArray: true },
        { label: 'Insurance Coverage', key: 'insuranceCoverage' },
        { label: 'Monthly Capacity', key: 'capacityVolume' }
      ]
    },
    {
      title: 'Compliance Documentation',
      icon: 'FileText',
      data: applicationData?.complianceDocumentation,
      fields: [
        { label: 'Business License', key: 'businessLicense', isFile: true },
        { label: 'Insurance Certificate', key: 'insuranceCertificate', isFile: true },
        { label: 'Tax Document', key: 'taxDocument', isFile: true },
        { label: 'Certifications', key: 'certifications', isFile: true }
      ]
    }
  ];

  const renderFieldValue = (field, data) => {
    const value = data?.[field?.key];
    
    if (!value) return <span className="text-muted-foreground italic">Not provided</span>;
    
    if (field?.isFile) {
      return (
        <div className="flex items-center gap-2">
          <Icon name="FileCheck" size={16} className="text-success" />
          <span className="text-foreground">{value?.name}</span>
        </div>
      );
    }
    
    if (field?.isArray) {
      return (
        <div className="flex flex-wrap gap-2">
          {value?.map((item, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
            >
              {item}
            </span>
          ))}
        </div>
      );
    }
    
    return <span className="text-foreground">{value}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Review & Submit</h2>
        <p className="text-muted-foreground">
          Please review your application carefully before submitting.
        </p>
      </div>
      {/* Error Alert */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}
      {/* Review Sections */}
      <div className="space-y-4">
        {sections?.map((section) => (
          <div key={section?.title} className="glass-container rounded-xl p-6">
            <div className="space-y-4">
              {/* Section Header */}
              <div className="flex items-center gap-3 pb-4 border-b border-white/20">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={section?.icon} size={20} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{section?.title}</h3>
              </div>

              {/* Section Fields */}
              <div className="space-y-3">
                {section?.fields?.map((field) => (
                  <div key={field?.key} className="flex flex-col sm:flex-row sm:items-start gap-2">
                    <span className="text-sm font-medium text-muted-foreground min-w-[180px]">
                      {field?.label}:
                    </span>
                    <div className="text-sm flex-1">
                      {renderFieldValue(field, section?.data)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Terms Confirmation */}
      <div className="glass-container rounded-xl p-6 bg-primary/5">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Application Submission</h4>
            <p className="text-sm text-muted-foreground">
              By submitting this application, you confirm that all information provided is accurate and complete. Our compliance team will review your application within 2-3 business days. You'll receive email updates about your application status.
            </p>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex justify-between pt-6 border-t border-white/20">
        <Button
          variant="outline"
          iconName="ArrowLeft"
          onClick={onBack}
          disabled={submitting}
        >
          Back
        </Button>
        <Button
          variant="success"
          iconName="Send"
          onClick={handleSubmit}
          loading={submitting}
        >
          Submit Application
        </Button>
      </div>
    </div>
  );
};

export default ReviewSubmitStep;